import { GridColumn as Column } from '@progress/kendo-react-grid';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  DeleteButton,
  SecondaryButton
} from 'src/components/shared/Button/Button';
import { BooleanCell } from 'src/components/shared/DataGrid/BooleanCell';
import { BooleanFilterCell } from 'src/components/shared/DataGrid/BooleanFilterCell';
import { DataGrid } from 'src/components/shared/DataGrid/DataGrid';
import { GridCountCell } from 'src/components/shared/DataGrid/GridCountCell';
import {
  HeaderSection,
  HeaderSectionLeft,
  HeaderSectionRight
} from 'src/components/shared/HeaderSection/HeaderSection';
import { Modal } from 'src/components/shared/Modal/Modal';
import { BodyType } from 'src/components/shared/Typography/BodyType';
import { Header1 } from 'src/components/shared/Typography/Header1';
import { useIsFormDirty } from 'src/hooks/useIsFormDirty';
import { useReduxDispatch } from 'src/store';
import { fetchProductTypes } from 'src/store/producttypes/producttypes.actions';
import { isLoadingSegments } from 'src/store/requests/requests.selectors';
import { SegmentDetailDto, SegmentDto } from 'src/store/segments/segment';
import { mapSegmentDetailToEdit } from 'src/store/segments/segment.map';
import {
  createSegment,
  deleteSegment,
  editSegment,
  fetchSegments
} from 'src/store/segments/segments.actions';
import { SegmentApi } from 'src/store/segments/segments.api';
import { selectSegments } from 'src/store/segments/segments.selectors';
import { SegmentForm } from './SegmentForm';

export const SegmentsPage = () => {
  const [currentSegment, setCurrentSegment] = useState<SegmentDto | null>(null);
  const [segment, setSegment] = useState<Nullable<SegmentDetailDto>>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [isFormDirty, callbackRef] = useIsFormDirty();

  const { t } = useTranslation();
  const dispatch = useReduxDispatch();

  const segments = useSelector(selectSegments);
  const loadingSegments = useSelector(isLoadingSegments);

  useEffect(() => {
    dispatch(fetchProductTypes());
    dispatch(fetchSegments());
  }, [dispatch]);

  useEffect(() => {
    if (currentSegment) {
      setShowDeleteModal(true);
    }
  }, [currentSegment]);

  const onCancel = () => {
    setCurrentSegment(null);
    setSegment(null);
    setShowDeleteModal(false);
    setShowEditModal(false);
  };

  const onCreate = () => {
    setShowEditModal(true);
  };

  const onEdit = async (segment: SegmentDto) => {
    const response = await SegmentApi.getById(segment.id);
    if (response.ok) {
      setSegment(response.data);
      setShowEditModal(true);
    }
  };

  const onDelete = (segment: SegmentDto) => {
    setCurrentSegment(segment);
  };

  const onConfirmDelete = async () => {
    if (currentSegment) {
      await dispatch(deleteSegment(currentSegment.id, currentSegment));
      setCurrentSegment(null);
      setShowDeleteModal(false);
    }
  };

  const onSubmit = async (segment: SegmentDetailDto) => {
    const dto = mapSegmentDetailToEdit(segment);
    if (dto.id) {
      await dispatch(editSegment(dto.id, dto));
    } else {
      await dispatch(createSegment(dto));
    }
    dispatch(fetchSegments());
    onCancel();
  };

  return (
    <>
      <HeaderSection className="mb-32">
        <HeaderSectionLeft>
          <Header1>{t('segment.plural')}</Header1>
          <BodyType>{`${t('crudtext')} ${t('segment.plural')}`}</BodyType>
        </HeaderSectionLeft>
        <HeaderSectionRight>
          <SecondaryButton onClick={onCreate} icon="la-plus">
            {`${t('create.verb')} ${t('segment.singular')}`}
          </SecondaryButton>
        </HeaderSectionRight>
      </HeaderSection>

      <DataGrid
        items={segments}
        loading={loadingSegments}
        onRowClick={({ dataItem }) => onEdit(dataItem)}
        initialSort={[{ field: 'no', dir: 'asc' }]}
      >
        <Column
          field="productTypeName"
          width={200}
          title={t('segment.productTypeName')}
          footerCell={() => (
            <GridCountCell
              count={segments?.length}
              label={t('segment.plural')}
            />
          )}
        />
        <Column field="no" width={200} title={t('props.number')} />
        <Column field="name" title={t('props.name')} />
        <Column field="description" title={t('props.description')} />
        <Column
          width="160px"
          field="active"
          title={t('props.active')}
          cell={props => <BooleanCell {...props} />}
          filterCell={props => <BooleanFilterCell {...props} />}
        />
        <Column
          width="160px"
          field="markLabel"
          title={t('segment.markLabel')}
          cell={props => <BooleanCell {...props} />}
          filterCell={props => (
            <BooleanFilterCell
              trueLabel={t('props.true')}
              falseLabel={t('props.false')}
              {...props}
            />
          )}
        />
        <Column
          width="100px"
          filterable={false}
          cell={props => (
            <td>
              <DeleteButton onClick={() => onDelete(props.dataItem)} />
            </td>
          )}
        />
      </DataGrid>

      <Modal
        width={1000}
        header={`${segment ? t('edit') : t('create.verb')} ${t(
          'segment.singular'
        )}`}
        confirmText={t('save.verb')}
        onClose={() => onCancel()}
        open={showEditModal}
        allowCommonExitStrategies={!isFormDirty}
      >
        <SegmentForm
          segment={segment}
          onCancel={onCancel}
          onSubmit={onSubmit}
          ref={callbackRef}
        />
      </Modal>

      <Modal
        width={500}
        header={`${t('delete.verb')} ${t('segment.singular')}`}
        confirmText={t('delete.verb')}
        onClose={() => onCancel()}
        onConfirm={() => onConfirmDelete()}
        open={showDeleteModal}
        showActions
      >
        <BodyType>
          {t('segment.delete')} <q>{currentSegment?.name}</q>?
        </BodyType>
      </Modal>
    </>
  );
};
